"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the types of toasts
export type ToastType = "success" | "error" | "warning" | "info";

// Define the shape of a toast message
export interface Toast {
    id: string;
    type: ToastType;
    message: string;
}

// Define the context interface
interface ToastContextType {
    toasts: Toast[];
    showToast: (type: ToastType, message: string) => void;
    hideToast: (id: string) => void;
}

// Create the context with default values
const ToastContext = createContext<ToastContextType>({
    toasts: [],
    showToast: () => {},
    hideToast: () => {},
});

// Create a provider component
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    // Add a new toast
    const showToast = (type: ToastType, message: string) => {
        const id = Date.now().toString();
        const newToast = { id, type, message };
        setToasts((prevToasts) => [...prevToasts, newToast]);

        // Auto-dismiss after 5 seconds (5000ms)
        setTimeout(() => {
            hideToast(id);
        }, 5000);
    };

    // Remove a toast by ID
    const hideToast = (id: string) => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    };

    return (
        <ToastContext.Provider value={{ toasts, showToast, hideToast }}>
            {children}
        </ToastContext.Provider>
    );
};

// Custom hook to use the toast context
export const useToast = () => useContext(ToastContext);
